import React, { useState } from "react";
import { useT } from "../i18n/LanguageContext";
import { SectionTag } from "../components/Section";
import { PDF } from "../i18n/photos";
import AfricaMap from "../components/AfricaMap";
import { COUNTRIES } from "../data/footprint";
import { X, MapPin, Calendar } from "lucide-react";

const DOCUMENT_PROGRAMS = [
  "Passport data pages (ICAO)",
  "National identity cards",
  "Driving licenses",
  "Tachograph cards",
  "Resident permits",
  "Voter cards",
  "Prison ID & management",
  "Civil registration documents",
];

const ENTERPRISE_CLIENTS = [
  "Telecommunications operators",
  "Tier-1 banks",
  "Insurance groups",
  "Mining & resources",
  "Public utilities",
  "Regional governments",
  "Media & broadcasters",
  "Healthcare systems",
];

export default function ProjectsPage() {
  const { t } = useT();
  const [selected, setSelected] = useState(null);

  return (
    <main className="pt-32 md:pt-40" data-testid="page-projects">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 py-16 md:py-24">
        <img src={PDF.flagsCollage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-22" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/70 via-brand-bg/90 to-brand-bg" />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <SectionTag>{t("projects.tag")}</SectionTag>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-medium tracking-tighter text-white sm:text-5xl lg:text-6xl">
            {t("projects.title")}
          </h1>
          <p className="mt-6 max-w-2xl text-base text-brand-textDim">{t("projects.sub")}</p>
        </div>
      </section>

      {/* Interactive map */}
      <section className="border-b border-white/10 py-20 md:py-28" data-testid="map-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="font-mono-tag">{t("map.tag")}</p>
              <h2 className="mt-4 font-heading text-3xl tracking-tight text-white sm:text-4xl">
                {t("map.title")}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-brand-textDim">{t("map.sub")}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-2 text-white">
                  <span className="h-3 w-3 bg-brand-red" />
                  {t("map.legend_served")}
                </span>
                <span className="inline-flex items-center gap-2 text-brand-textDim">
                  <span className="h-3 w-3 border border-white/30 bg-[#1a1a1a]" />
                  {t("map.legend_other")}
                </span>
              </div>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-textDim">
                {COUNTRIES.length} {t("map.programs_label")}
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="relative border border-white/10 bg-[#0A0A0A] p-4 sm:p-8">
                <AfricaMap onSelect={setSelected} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Country case studies */}
      <section className="border-b border-white/10 py-20 md:py-28" data-testid="case-studies">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono-tag">{t("cases.tag")}</p>
              <h2 className="mt-4 font-heading text-3xl tracking-tight text-white sm:text-4xl">
                {t("cases.title")}
              </h2>
            </div>
            <p className="max-w-md text-sm text-brand-textDim">{t("cases.sub")}</p>
          </div>

          <ul className="mt-12 grid gap-px border border-white/10 bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
            {COUNTRIES.map((c, i) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => setSelected(c)}
                  data-testid={`case-${c.code.toLowerCase()}`}
                  className="group flex h-full w-full flex-col gap-4 bg-brand-bg p-6 text-left transition-colors hover:bg-[#0E0E0E]"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-brand-textDim">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-2xl leading-none">{c.flag}</span>
                  </div>
                  <h3 className="font-heading text-xl tracking-tight text-white">
                    {c.name}
                    {c.headquartered && (
                      <span className="ms-2 inline-flex items-center font-mono text-[9px] tracking-[0.22em] text-brand-red">
                        HQ
                      </span>
                    )}
                  </h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-red">
                    {c.program}
                  </p>
                  <p className="text-sm leading-relaxed text-brand-textDim line-clamp-3">{c.summary}</p>
                  <span className="mt-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 transition-colors group-hover:text-brand-red">
                    <Calendar className="h-3 w-3" /> {c.yearsLabel}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Documents + Enterprise — kept */}
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
          <div className="grid gap-px border border-white/10 bg-white/5 lg:grid-cols-2">
            <Column title={t("projects.docs_title")} items={DOCUMENT_PROGRAMS} tag={t("projects.docs_label")} testId="projects-docs" />
            <Column title={t("projects.enterprise_title")} items={ENTERPRISE_CLIENTS} tag={t("projects.partners_label")} testId="projects-enterprise" />
          </div>
        </div>
      </section>

      {/* Case study modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          data-testid="case-modal"
          onClick={() => setSelected(null)}
        >
          <div
            className="relative w-full max-w-2xl border border-white/15 bg-[#0A0A0A] p-6 sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="absolute end-3 top-3 border border-white/15 p-2 text-white hover:border-brand-red hover:text-brand-red"
              aria-label="Close"
              data-testid="case-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-4">
              <span className="text-4xl leading-none">{selected.flag}</span>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-textDim">
                  <MapPin className="me-1 inline h-3 w-3" /> {selected.name}
                  {selected.headquartered && <span className="ms-2 text-brand-red">· HQ</span>}
                </p>
                <h3 className="mt-1 font-heading text-2xl tracking-tight text-white sm:text-3xl">
                  {selected.program}
                </h3>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-brand-textDim">
              <Calendar className="h-3.5 w-3.5" />
              {selected.yearsLabel}
            </div>

            <p className="mt-6 border-l-2 border-brand-red bg-[#0E0E0E] p-4 text-sm leading-relaxed text-white/90">
              {selected.summary}
            </p>

            <a
              href="/contact"
              className="mt-8 inline-flex items-center gap-2 bg-brand-red px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white hover:bg-brand-redHover"
              data-testid="case-modal-cta"
            >
              {t("cases.cta")}
            </a>
          </div>
        </div>
      )}
    </main>
  );
}

function Column({ title, items, tag, testId }) {
  return (
    <div className="bg-brand-bg p-8 md:p-10" data-testid={testId}>
      <p className="font-mono-tag">{tag}</p>
      <h3 className="mt-5 font-heading text-2xl tracking-tight text-white">{title}</h3>
      <ul className="mt-8 space-y-3">
        {items.map((it, i) => (
          <li key={it} className="flex items-start gap-3 border-b border-white/5 py-2.5 text-sm text-white/85">
            <span className="font-mono text-[10px] text-brand-textDim">{String(i + 1).padStart(2, "0")}</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
